// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#417) — pure request-body builder for the query Lambda's
// `resolveInvoiceFromEventLog` method: the FALLBACK resolve used when an
// invoice no longer has an ActiveQueue document.
//
// Why this exists: ActiveQueue is a working queue, not an archive. Jobs leave
// it. Ticket Master resolves invoice → ActiveQueue doc and gates all three
// lanes on that doc's ObjectId, so a purged job renders the tool completely
// empty — for exactly the finished/complained-about tickets a help-desk person
// opens it to research, and silently (an empty screen reads as "no history").
//
// The link survives the purge: every EventLog entry carries
//   documentKey: { _id, _kf_KellerID, invoiceNo, location }
// stamped by the change-stream trigger, and the final `delete` event's `before`
// image is the complete job document as it last existed.
//
// ⚠️ Deliberately NOT `browseCollection` (the generic browse this file's sibling
// `buildResolveInvoiceRequestBody` uses). browseCollection runs
// `countDocuments(filter)` before `find()` — two full scans of a 607k-document
// EventLog, measured at 28.3s against PROD, i.e. AT the API Gateway 29s
// ceiling. The dedicated method does one `.limit(1)` query with no count.
//
// Pure + tested so the query shape can't silently regress.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const VALID_DATABASES = ["Warehouses", "UAT"];

/**
 * @param {object} p
 * @param {string|number} p.invoice — the typed invoice number
 * @param {string} p.database       — "Warehouses" | "UAT". NOTE: the Lambda
 *                                     forces "Warehouses" for this method
 *                                     (EventLog only exists there), same as the
 *                                     other Time Machine reads — passed for
 *                                     consistency and dispatcher routing.
 * @returns {object} resolveInvoiceFromEventLog request body
 */
export function buildResolveInvoiceFromEventLogRequestBody({
  invoice,
  database,
} = {}) {
  const value = invoice == null ? "" : String(invoice).trim();
  if (value === "") {
    throw new Error("buildResolveInvoiceFromEventLogRequestBody: invoice is required");
  }
  if (!VALID_DATABASES.includes(database)) {
    throw new Error(
      `buildResolveInvoiceFromEventLogRequestBody: database must be one of ${VALID_DATABASES.join(", ")} (got ${JSON.stringify(database)})`
    );
  }

  return {
    method: "resolveInvoiceFromEventLog",
    database,
    collection: "AuditSnapshots", // dispatcher artifact — the handler reads EventLog
    invoice: value,
  };
}

export const __testables__ = { VALID_DATABASES };
